import React, { FC, useEffect, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { Box, Button, InputLabel } from "@mui/material";
import { LocalizationLanguage } from "@green0meter/api-client";
import { object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { isNumber } from "lodash";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

import Drawer from "@components/common/Drawer";
import { CircularProgress, TextField } from "@components/base";
import { LanguageTabs, RichTextBox } from "@components/common";
import {
  useCreateFormSectionConfig,
  useGetFormSectionConfigDetails,
  useUpdateFormSectionConfig,
} from "@api";
import { useMultistepFormStore } from "@components/common/MultistepForm/MultistepForm.store";
import { useUserStore } from "@store/user";

const schema = object({
  name: string().min(1).max(255).required(t("validation.required")),
  description: string().min(0).max(500).optional(),
});

const FormSectionEditor: FC = () => {
  const {
    activeSection,
    addSectionOpen,
    setAddSectionOpen,
    editSectionOpen,
    setEditSectionOpen,
    formSettings: { formBasics },
  } = useMultistepFormStore();
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { createFormSectionConfig } = useCreateFormSectionConfig(
    user,
    formBasics.id,
  );
  const { updateFormSectionConfig } = useUpdateFormSectionConfig(
    user,
    formBasics.id,
  );
  const { formSectionConfigDetails, isFetchingFormSectionConfigDetails } =
    useGetFormSectionConfigDetails(
      user,
      activeSection?.id || "",
      !!activeSection && editSectionOpen && !addSectionOpen,
    );

  const methods = useForm<{ name: string; description?: string }>({
    mode: "onChange",
    resolver: yupResolver(schema),
  });
  const watch = useWatch({
    control: methods.control,
    name: ["name", "description"],
  });
  const [selectedLanguage, setSelectedLanguage] = useState(LocalizationLanguage.EN);
  const [translations, setTranslations] = useState(
    Object.values(LocalizationLanguage)
      .filter((key) => isNumber(key))
      .map((key) => ({
        name: "",
        description: "",
        language: key as LocalizationLanguage,
      })),
  );

  const handleSubmit = (): void => {
    const values = methods.getValues();

    if (editSectionOpen && activeSection) {
      updateFormSectionConfig({
        sectionId: activeSection.id,
        dto: {
          code: activeSection.code,
          translations,
        },
      });
    } else {
      createFormSectionConfig({
        formId: formBasics.id,
        code: values.name,
        translations,
      });
    }

    onClose();
  };

  const onClose = (): void => {
    setEditSectionOpen(false);
    setAddSectionOpen(false);
    methods.reset();
  };

  useEffect(() => {
    if (editSectionOpen) {
      const translationDetails =
        formSectionConfigDetails?.translations.find(
          (t) => t.language === selectedLanguage,
        ) || translations.find((t) => t.language === selectedLanguage);

      methods.setValue("name", translationDetails?.name || "");
      methods.setValue("description", translationDetails?.description || "");
    } else {
      methods.setValue("name", "");
      methods.setValue("description", "");
    }
  }, [selectedLanguage, formSectionConfigDetails, editSectionOpen, addSectionOpen]);

  useEffect(() => {
    if (formSectionConfigDetails) {
      const newTranslations = translations.map((translation) => {
        const translationDetails = formSectionConfigDetails.translations.find(
          (t) => t.language === translation.language,
        );

        return {
          ...translation,
          name: translationDetails?.name || "",
          description: translationDetails?.description || "",
        };
      });

      setTranslations(newTranslations);
    }
  }, [formSectionConfigDetails]);

  useEffect(() => {
    setTranslations((prevTranslations) =>
      prevTranslations.map((translation) => {
        if (translation.language === selectedLanguage) {
          return {
            ...translation,
            name: watch[0],
            description: watch[1] || "",
          };
        }

        return translation;
      }),
    );
  }, [watch]);

  return (
    <Drawer
      title={editSectionOpen ? "Edit question group" : "Create question group"}
      open={editSectionOpen || addSectionOpen}
      onClose={onClose}
      content={
        editSectionOpen && isFetchingFormSectionConfigDetails ? (
          <CircularProgress />
        ) : (
          <FormProvider {...methods}>
            <form
              onReset={() => {
                methods.reset();
                onClose();
              }}
              style={{
                display: "flex",
                flex: 1,
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <LanguageTabs
                  language={selectedLanguage}
                  setLanguage={setSelectedLanguage}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <InputLabel sx={{ fontWeight: "bold" }}>
                    {t("multistep.editor.questionGroupName")}
                  </InputLabel>
                  <Controller
                    control={methods.control}
                    name="name"
                    render={({
                      field,
                      fieldState: { error },
                    }): React.ReactElement => (
                      <TextField
                        {...field}
                        error={Boolean(error)}
                        helperText={error?.message}
                        color="secondary"
                        variant="filled"
                        placeholder={t("multistep.editor.questionGroupName")}
                        fullWidth
                      />
                    )}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <InputLabel sx={{ fontWeight: "bold" }}>
                    {t("common.description")}
                  </InputLabel>
                  <Controller
                    control={methods.control}
                    name="description"
                    render={({ field }): React.ReactElement => (
                      <RichTextBox
                        {...field}
                        value={field.value ?? ""}
                        placeholder={t("common.description")}
                      />
                    )}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button type="reset" variant="outlined">
                  {t("common.cancel")}
                </Button>
                <Button disabled={!methods.formState.isValid} onClick={handleSubmit}>
                  {editSectionOpen
                    ? t("common.saveChanges")
                    : t("multistep.editor.createQuestionGroup")}
                </Button>
              </Box>
            </form>
          </FormProvider>
        )
      }
    />
  );
};

export default FormSectionEditor;
